import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, XCircle, FileDown, Loader2, MapPin } from "lucide-react";

const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmt = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const API = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm py-2 border-b border-slate-100 last:border-0">
    <span className="text-slate-500 shrink-0">{label}</span><span className="font-medium text-slate-800 text-right">{value || "—"}</span>
  </div>
);

const fmtDt = (v) => { if (!v) return "—"; const d = new Date(v); return isNaN(d) ? v : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };

export default function PublicPreventivo({ evento = false }) {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(params.get("scelta") === "rifiuta" ? "rifiutato" : params.get("scelta") === "accetta" ? "accettato" : null);
  const base = evento ? "/public/preventivo-evento" : "/public/preventivo";

  useEffect(() => { api.get(`${base}/${token}`).then((r) => setD(r.data)).catch(() => setErr("Link non valido o scaduto.")); }, [token, base]);

  const rispondi = async (decisione) => {
    setBusy(true);
    try { const { data } = await api.post(`${base}/${token}/risposta`, { decisione, nota }); setD((p) => ({ ...p, stato: data.stato, risposta_at: new Date().toISOString() })); setConfirm(null); }
    catch (e) { setErr(e.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  const chiuso = d && (d.stato === "accettato" || d.stato === "rifiutato");
  const totale = evento ? d?.totale : d?.calcolo?.totale;

  return (
    <div className="min-h-screen bg-[#F2F4F5] flex flex-col items-center px-4 py-10">
      <img src="/nexus_logo.png" alt="Nexus Italia Group" className="h-11 w-auto object-contain mb-6" />
      <div className="w-full max-w-lg nx-fade-in">
        <div className="bg-primary text-white rounded-t-lg px-5 py-3 border-b-2 border-nexus-gold">
          <p className="text-xs tracking-widest text-white/80">{evento ? "PREVENTIVO ASSISTENZA SANITARIA EVENTO" : "PREVENTIVO TRASPORTO SANITARIO"}</p>
          <p className="font-semibold font-heading" data-testid="public-prev-codice">{d?.codice || "…"}</p>
        </div>
        <Card className="rounded-t-none border-slate-200 p-5">
          {err && !d ? <p data-testid="public-prev-error" className="text-center text-slate-500 py-10 text-sm">{err}</p>
            : !d ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            : (
              <>
                <p className="text-sm text-slate-700 mb-3">Gentile <b>{d.cliente.nominativo}</b>, di seguito il riepilogo del preventivo del {fmt(d.data)}.</p>
                {evento ? (
                  <>
                    <Row label="Evento" value={d.denominazione} />
                    <Row label={<span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Luogo</span>} value={d.luogo} />
                    <Row label="Inizio" value={fmtDt(d.inizio)} />
                    <Row label="Fine" value={fmtDt(d.fine)} />
                    <Row label="Durata servizio" value={`${d.ore} ore`} />
                    <Row label="Mezzi e risorse" value={d.risorse?.join(", ")} />
                    <div className="mt-4 rounded-md border border-slate-200 overflow-hidden">
                      <table className="w-full text-sm"><tbody>
                        <tr className="bg-[#F3ECDC]"><td className="px-3 py-2.5 font-bold text-slate-900">TOTALE PREVENTIVO</td><td data-testid="public-prev-totale" className="px-3 py-2.5 text-right font-bold text-primary text-lg">{eur(totale)}</td></tr>
                      </tbody></table>
                    </div>
                  </>
                ) : (
                  <>
                <Row label="Data del trasporto" value={fmt(d.data_trasporto)} />
                <Row label="Mezzo" value={d.mezzo} />
                <Row label="Tratta" value={d.tratta} />
                {d.punti.map((p, i) => <Row key={`${i}-${p}`} label={<span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Punto {i + 1}</span>} value={p} />)}
                <Row label="Distanza" value={`${d.km} km`} />
                <div className="mt-4 rounded-md border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm"><tbody>
                    {d.calcolo.voci?.map((v, i) => <tr key={`${i}-${v.voce}`} className="border-b border-slate-100"><td className="px-3 py-2 text-slate-700">{v.voce}</td><td className="px-3 py-2 text-right font-medium">{eur(v.totale)}</td></tr>)}
                    <tr className="bg-[#F3ECDC]"><td className="px-3 py-2.5 font-bold text-slate-900">TOTALE</td><td data-testid="public-prev-totale" className="px-3 py-2.5 text-right font-bold text-primary text-lg">{eur(d.calcolo.totale)}</td></tr>
                  </tbody></table>
                </div>
                  </>
                )}
                {d.note && <p className="text-xs text-slate-500 mt-3"><b>Note:</b> {d.note}</p>}
                <a data-testid="public-prev-pdf" href={`${API}/api${base}/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:text-nexus-gold mt-3 underline"><FileDown className="h-4 w-4" /> Scarica il PDF</a>

                <div className="mt-6 pt-5 border-t border-slate-200">
                  {chiuso ? (
                    <div data-testid="public-prev-esito" className={`flex items-center gap-2 rounded-md px-4 py-3 text-sm font-semibold ${d.stato === "accettato" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                      {d.stato === "accettato" ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                      Preventivo {d.stato} {d.risposta_at && `il ${fmt(d.risposta_at)}`}. Grazie, la Centrale Operativa è stata informata.
                    </div>
                  ) : confirm ? (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-slate-800">Confermi di voler <span className={confirm === "accettato" ? "text-emerald-700" : "text-red-700"}>{confirm === "accettato" ? "accettare" : "rifiutare"}</span> il preventivo {d.codice}?</p>
                      <Textarea data-testid="public-prev-nota" rows={3} placeholder="Nota facoltativa per la Centrale Operativa..." value={nota} onChange={(e) => setNota(e.target.value)} />
                      {err && <p className="text-sm text-red-700">{err}</p>}
                      <div className="flex gap-2">
                        <Button data-testid="public-prev-conferma" className="flex-1 gap-2" variant={confirm === "accettato" ? "default" : "destructive"} disabled={busy} onClick={() => rispondi(confirm)}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Conferma</Button>
                        <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Annulla</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <Button data-testid="public-prev-accetta" className="flex-1 gap-2" onClick={() => setConfirm("accettato")}><CheckCircle2 className="h-4 w-4" /> Accetta il preventivo</Button>
                      <Button data-testid="public-prev-rifiuta" variant="outline" className="flex-1 gap-2 text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800" onClick={() => setConfirm("rifiutato")}><XCircle className="h-4 w-4" /> Rifiuta</Button>
                    </div>
                  )}
                </div>
              </>
            )}
        </Card>
        <p className="text-center text-xs text-slate-400 mt-6">Nexus Italia Group · Centrale Operativa 0287177344</p>
      </div>
    </div>
  );
}
